import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ArrowRight, ChevronDown, Sun, Moon, Globe } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useTheme } from "../i18n/ThemeContext";
import { Link, NavLink } from "./LocalizedLink";
import { localizePath, stripLocalePrefix } from "../i18n/localePath";
import { useVersion } from "../version/VersionContext";
import navExtra from "../i18n/navExtra";
import "../css/nav.css";
import ViewSettings from "./ViewSettings";
import LanguageSettings from "./LanguageSettings";
import { v3Menus } from "../v3/menu";
import { v4Label, V4_HIDDEN } from "../v4/labels";
import { v4ProductCols } from "../v4/menu";
import { v3Page } from "../v3/pages.js";
import v3Kontakt from "../v3/content/kontakt";
import { track as v3Track } from "../v3/lead/track";
import "../v3/css/lead.css";

// Navigation beider Versionen.
// Version 2: Utility Leiste und große Mega Menüs im Konzernstil. Version 1: Mega Menüs der bisherigen Live Seite im neuen Styleguide.
// Nur echte Mausgeräte bekommen das Abdunkeln beim Überfahren, Touch löst nach dem Tippen sonst ein künstliches Hover aus
// Service Leiste über der Hauptnavigation (Version 2), derzeit aus
const SHOW_UTIL_BAR = false;

const canHover = () => typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Adresse ohne Anker und Abfrage, ohne Schrägstrich am Ende
const pathOf = (to) => (to.split(/[?#]/)[0].replace(/\/$/, "") || "/");
const hashOf = (to) => { const i = to.indexOf("#"); return i < 0 ? "" : to.slice(i); };

// Menüdaten je Version zusammenstellen: { key: { title, intro, all, cols: [{ title, items, sub }], teaser } }
const buildMenus = (version, t, x, lang) => {
    const n = t.r.nav;
    // Version 3 und 4: Menü aus src/v3/menu.js (V4 übernimmt die Sitemap)
    if (version === "3") return v3Menus(lang);
    if (version === "4") {
        // V4: eigene Bezeichnungen (Magazin, Presse), Adressen gleich
        const k = lang === "en" ? "en" : "de";
        const m = v3Menus(lang);
        Object.values(m.menus).forEach((menu) => menu.cols.forEach((c) => { c.items = c.items.filter((i) => !V4_HIDDEN.has(i.to)); c.items.forEach((i) => { i.label = v4Label(i.to, k, i.label); }); }));
        // 29.10.2026: Produkte und Anlagen mit drei Gruppen (Komponenten, Maschinen, Hersteller) nach dem V4 Katalog
        if (m.menus.produkte) m.menus.produkte = { ...m.menus.produkte, cols: v4ProductCols() };
        return m;
    }
    if (version === "1") {
        const m = x.v1.menus;
        return {
            links: [
                { to: "/infrastructure", label: t.nav.infrastructure, id: "v1-infrastructure", menu: "infrastructure" },
                { to: "/solutions", label: t.nav.solutions, id: "v1-solutions", menu: "solutions" },
                { to: "/by-role", label: t.nav.roles, id: "v1-roles", menu: "roles" },
                { to: "/insights", label: t.nav.insights, id: "v1-insights", menu: "insights" },
                { to: "/company", label: t.nav.company, id: "v1-company", menu: "company" },
            ],
            menus: m,
        };
    }
    // Version 2 schlank (Wunsch 16.10.2026): zwei einfache Links, zwei kleine Menüs, keine Bilder.
    // Leistungen, Branchen, Presse, Qualität und Standort stehen in der Fußzeile und auf den Übersichtsseiten.
    const m = x.v2.menus;
    return {
        links: [
            { to: "/how-it-works", label: n.how, id: "how" },
            { to: "/solutions", label: n.stock, id: "stock", menu: "stock" },
            { to: "/references", label: n.references, id: "references" },
            { to: "/company", label: n.about, id: "about", menu: "about" },
        ],
        menus: {
            stock: {
                ...m.stock,
                teaser: null,
                cols: [{ title: m.stock.colStock, items: n.stockItems }],
            },
            about: {
                ...m.about,
                teaser: null,
                cols: [{ title: m.about.colCompany, items: [...n.aboutItems, ...m.about.extra.filter((e) => e.to === "/sustainability" || e.to === "/brands")] }],
            },
        },
    };
};

// Alle Ziele eines Menüs (für Aktiv Markierung und Burger Menü)
const itemsOf = (menu) => menu.cols.flatMap((c) => [...c.items, ...(c.sub ? c.sub.items : [])]);

const NavbarLegacy = () => {
    const { lang, uiLang, setLang, t } = useLanguage();
    // Version 4: Sprachwahl folgt der Adresse (uiLang), Inhalte bleiben deutsch und werden übersetzt
    const langUi = uiLang || lang;
    const n = t.r.nav;
    const x = navExtra[lang] || navExtra.de;
    const { theme, toggleTheme } = useTheme();
    const { version } = useVersion();
    const [scrolled, setScrolled] = useState(false);
    // Version 3: ganz oben auf der Startseite (für die transparente Leiste über dem dunklen Hero)
    const [atTop, setAtTop] = useState(true);
    const [open, setOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const [hover, setHover] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [mobileGroup, setMobileGroup] = useState(null);
    const langRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Kompakt beim Runterscrollen, sofort wieder groß, sobald nach oben gescrollt wird.
        let last = window.scrollY;
        const onScroll = () => {
            const y = window.scrollY;
            setAtTop(y <= 40);
            if (y <= 40) setScrolled(false);
            else if (y > last + 4) setScrolled(true);
            else if (y < last - 4) setScrolled(false);
            if (Math.abs(y - last) > 4 || y <= 40) last = y;
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Touch: Tipp außerhalb der Leiste setzt Untermenü und Abdunkeln zurück
    useEffect(() => {
        const onDown = (e) => {
            if (e.pointerType === "mouse") return;
            if (!e.target.closest || !e.target.closest('[data-testid="main-navbar"]')) { setOpenMenu(null); setHover(false); }
        };
        document.addEventListener("pointerdown", onDown);
        return () => document.removeEventListener("pointerdown", onDown);
    }, []);

    useEffect(() => {
        setOpen(false);
        setHover(false);
        setOpenMenu(null);
        setLangOpen(false);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        if (!langOpen) return;
        const onDown = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [langOpen]);

    // Version 3: Auslöser der Mega Menüs (Tastatur) und Burger Knopf für den Fokus nach dem Schließen
    const megaTriggers = useRef({});
    const burgerRef = useRef(null);

    // Escape schließt Mega Menü und Burger Menü (Version 3: Fokus zurück auf den Auslöser)
    useEffect(() => {
        if (!openMenu && !open) return;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            if ((version === "3" || version === "4")) {
                if (openMenu) megaTriggers.current[openMenu]?.focus();
                else if (open) burgerRef.current?.focus();
            }
            setOpenMenu(null);
            setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openMenu, open]);

    // Version 3, Burger offen: Seite und Fußzeile inert (Fokus bleibt im Menü), beim Schließen Fokus zurück auf den Burger
    const wasOpen = useRef(false);
    useEffect(() => {
        if (version !== "3") return undefined;
        const els = [...document.querySelectorAll("main, footer")];
        if (open) els.forEach((el) => { el.inert = true; });
        else if (wasOpen.current) burgerRef.current?.focus({ preventScroll: true });
        wasOpen.current = open;
        return () => els.forEach((el) => { el.inert = false; });
    }, [open, version]);

    // Version 3: Escape in Einstellungen oder Sprache setzt den Fokus zurück auf deren Auslöser
    useEffect(() => {
        if (version !== "3") return undefined;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            const wrap = document.activeElement?.closest?.(".vs-wrap");
            if (wrap) setTimeout(() => wrap.querySelector(".vs-trigger")?.focus(), 0);
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [version]);

    // Version 3: Mega Menü per Tastatur öffnen und den Fokus auf den ersten Eintrag setzen
    const openMegaByKey = (id) => {
        setOpenMenu(id);
        setTimeout(() => document.querySelector("#nv-mega-panel a")?.focus(), 80);
    };
    // Sprung zum Inhalt (Version 3): Hash Router taugt nicht für #anker, deshalb Fokus per Skript
    const skipToMain = (e) => {
        e.preventDefault();
        const main = document.querySelector("main");
        if (!main) return;
        if (!main.id) main.id = "inhalt";
        main.tabIndex = -1;
        main.focus();
    };

    const { links, menus } = buildMenus(version, t, x, lang);
    // Aktiver Bereich: alle Unterseiten eines Menüs markieren den Hauptpunkt (Kontakt und Startseite ausgenommen)
    const path = stripLocalePrefix(location.pathname).replace(/\/$/, "") || "/";
    // Version 4 (K14): auf jeder Seite mit dunklem Kopf (erstes Element in main mit Klasse on-deep) wie auf der Startseite transparent.
    // Prüft nach jedem Seitenwechsel und, solange die Seite nachlädt, bei jeder Änderung der Seitenstruktur.
    const [darkHead, setDarkHead] = useState(false);
    useEffect(() => {
        if (version !== "4") return undefined;
        const check = () => {
            const first = document.querySelector("main")?.firstElementChild;
            let dark = Boolean(first && first.classList.contains("on-deep"));
            if (first && !dark) {
                // auch Köpfe mit dunkler Hintergrundfarbe ohne Klasse (z. B. Impressum)
                const m = getComputedStyle(first).backgroundColor.match(/\d+(\.\d+)?/g);
                if (m && (m[3] === undefined || Number(m[3]) > 0.5)) dark = (0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2]) / 255 < 0.3;
            }
            setDarkHead(dark);
        };
        check();
        // Nachgeladene Seiten: bei jeder Änderung der Seitenstruktur erneut prüfen (gebündelt pro Frame)
        let raf = 0;
        const mo = new MutationObserver(() => { if (!raf) raf = window.requestAnimationFrame(() => { raf = 0; check(); }); });
        const root = document.getElementById("root") || document.body;
        mo.observe(root, { childList: true, subtree: true });
        return () => { mo.disconnect(); if (raf) window.cancelAnimationFrame(raf); };
    }, [path, version]);
    const groupPaths = (l) => {
        const own = l.menu && menus[l.menu] ? itemsOf(menus[l.menu]).map((i) => pathOf(i.to)) : [];
        return [pathOf(l.to), ...own].filter((p) => p !== "/" && p !== "/contact");
    };
    const isGroupActive = (l) => groupPaths(l).some((pre) => path === pre || path.startsWith(`${pre}/`));
    const isCurrent = (to) => {
        const p = pathOf(to);
        const h = hashOf(to);
        // Version 3: Einträge mit Anker nur bei passendem Anker markieren, der Eintrag ohne Anker nur ohne Anker in der Adresse
        if ((version === "3" || version === "4")) return path === p && (h ? location.hash === h : !location.hash);
        return path === p && (!h || location.hash === h);
    };

    // Burger: beim Öffnen die Gruppe der aktuellen Seite aufklappen
    useEffect(() => {
        if (!open) return;
        const active = links.find((l) => isGroupActive(l));
        setMobileGroup(active ? active.id : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const switchLanguage = (nextLang) => {
        const target = localizePath(`${location.pathname}${location.search}${location.hash}`, nextLang);
        setLang(nextLang);
        navigate(target);
        setLangOpen(false);
    };

    // Version 3, Startseite und Landingpages: über dem dunklen Hero transparent mit weißem Logo, bis gescrollt oder ein Menü geöffnet wird
    const overDark = (version === "3" || version === "4") && (path === "/" || v3Page(path)?.template === "landing" || (version === "4" && darkHead)) && atTop && !open && !openMenu;
    const logo = theme === "dark" || overDark ? "/brand/parto-logo-negativ.svg" : "/brand/parto-logo-positiv.svg";
    const util = version === "2" ? x.v2.util : null;
    const menu = openMenu ? menus[openMenu] : null;

    // Version 3: Button „Anfrage senden“ auf /kontakt, Klick wird gemessen (nur Version 3)
    const navCta = n.cta;
    const v3Cta = (v3Kontakt[lang] || v3Kontakt.de).nav.cta;
    const v3CtaClick = (where) => () => v3Track("cta_click", { location: where, target: "/kontakt" });

    const themeLabel = lang === "en" ? (theme === "dark" ? "Switch to light mode" : "Switch to dark mode") : (theme === "dark" ? "Hellen Modus einschalten" : "Dunklen Modus einschalten");

    // Einzelner Eintrag im Mega Menü
    const megaItem = (item, testId) => {
        const cur = isCurrent(item.to);
        return (
            <li key={item.to}>
                <Link to={item.to} data-testid={testId} aria-current={cur ? "page" : undefined} className={`nv-item ${cur ? "is-current" : ""}${item.kind ? ` is-${item.kind}` : ""}`}>
                    <span className="nv-item-label">
                        {item.label}
                        <ArrowRight size={14} className="nv-item-arrow" aria-hidden="true" />
                    </span>
                    {item.desc && <span className="nv-item-desc">{item.desc}</span>}
                </Link>
            </li>
        );
    };

    // Sprache (auch in Version 3 sichtbar)
    const langControl = (
                    <div className="relative" data-testid="language-dropdown" ref={langRef}>
                        <button
                            type="button"
                            data-testid="language-dropdown-trigger"
                            onClick={() => setLangOpen((o) => !o)}
                            aria-haspopup="listbox"
                            aria-expanded={langOpen}
                            className="rl-nav-ctl flex items-center gap-1.5 border border-petrol-700 px-3 text-sm font-bold text-ink hover:border-ink transition-colors duration-300"
                        >
                            <Globe size={13} className="text-petrol-400" />
                            {lang.toUpperCase()}
                        </button>
                        <AnimatePresence>
                            {langOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.2 }}
                                    role="listbox"
                                    data-testid="language-dropdown-menu"
                                    className="nv-lang-menu absolute right-0 top-full mt-2 min-w-[140px] z-50 border border-petrol-700 bg-petrol-950 shadow-[0_20px_50px_rgba(7,48,43,0.15)]"
                                >
                                    {[{ code: "de", label: "Deutsch" }, { code: "en", label: "English" }].map((o) => (
                                        <button
                                            key={o.code}
                                            type="button"
                                            role="option"
                                            aria-selected={langUi === o.code}
                                            data-testid={`language-option-${o.code}`}
                                            onClick={() => switchLanguage(o.code)}
                                            className={`w-full px-4 py-2.5 text-left text-sm transition-colors duration-200 ${langUi === o.code ? "bg-brand text-white" : "text-petrol-300 hover:text-ink hover:bg-petrol-800"}`}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
    );

    // Hell/Dunkel und Sprache
    const controls = (
        <>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={theme === "dark"}
                        aria-label={themeLabel}
                        data-testid="theme-switch"
                        onClick={toggleTheme}
                        className="rl-nav-ctl nv-switch relative w-[68px] shrink-0 border border-petrol-700 bg-transparent transition-colors duration-300 hover:border-ink"
                    >
                        <span className={`nv-switch-knob absolute top-[4px] bottom-[4px] w-[28px] bg-brand transition-[left] duration-300 ease-out ${theme === "dark" ? "left-[34px]" : "left-[4px]"}`} aria-hidden="true" />
                        <Sun size={14} className={`absolute left-[11px] top-1/2 -translate-y-1/2 z-10 ${theme === "light" ? "text-white" : "text-petrol-400"}`} />
                        <Moon size={13} className={`absolute right-[11px] top-1/2 -translate-y-1/2 z-10 ${theme === "dark" ? "text-white" : "text-petrol-400"}`} />
                    </button>

                    {langControl}
        </>
    );

    return (
        <>
        {/* Version 3: Sprung zum Inhalt als erstes Tastaturziel */}
        {(version === "3" || version === "4") && <a href="#inhalt" className="v3-skip" onClick={skipToMain} data-testid="v3-skip-link">{lang === "en" ? "Skip to content" : "Zum Inhalt springen"}</a>}
        {/* Abdunkeln der restlichen Seite, solange die Maus auf der Navigation ist oder ein Menü offen ist */}
        {/* Version 3: Abdunkeln nur bei offenem Mega Menü oder Burger, schwächer (.15) */}
        <div className={`rl-nav-scrim ${((version === "3" || version === "4") ? openMenu || open : hover || openMenu || open) ? "is-on" : ""} ${open ? "is-block" : ""} ${(version === "3" || version === "4") ? "is-v3" : ""}`} aria-hidden="true" onClick={() => setOpen(false)} />
        <div className={`rl-nav-wrap ${scrolled ? "is-compact" : ""}`}>
        <header
            data-testid="main-navbar"
            data-nav-version={version}
            onPointerEnter={(e) => e.pointerType === "mouse" && canHover() && setHover(true)}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") { setOpenMenu(null); setHover(false); } }}
            onBlur={(version === "3" || version === "4") ? (e) => { if (openMenu && !e.currentTarget.contains(e.relatedTarget)) setOpenMenu(null); } : undefined}
            className={`rl-nav rl-glass nv-nav ${scrolled ? "is-compact" : ""} ${openMenu || open ? "is-open" : ""} ${overDark ? "is-over-dark" : ""}`}
        >
            {/* Version 2: schmale Service Leiste. Auf Wunsch vom 16.10.2026 wieder entfernt, die Navigation ist wie ursprünglich einzeilig.
                Zum Wiedereinschalten SHOW_UTIL_BAR auf true setzen. */}
            {SHOW_UTIL_BAR && util && (
                <div
                    className={`nv-util hidden xl:block ${scrolled ? "is-hidden" : ""}`}
                    data-testid="nav-utility"
                    aria-hidden={scrolled ? "true" : undefined}
                    inert={scrolled ? true : undefined}
                    onMouseEnter={() => setOpenMenu(null)}
                >
                    <div className="nv-util-row">
                        <p className="nv-util-claim">
                            <span>{util.claim}</span>
                            <span className="nv-util-sep" aria-hidden="true" />
                            <Link to="/brands" className="nv-util-link" data-testid="nav-util-brands">{util.brandsHint}</Link>
                        </p>
                        <nav aria-label={x.ui.utilLabel} className="nv-util-nav">
                            {util.links.map((l) => (
                                <Link key={l.to} to={l.to} data-testid={`nav-util-${pathOf(l.to).slice(1)}`} aria-current={isCurrent(l.to) ? "page" : undefined} className={`nv-util-link ${isCurrent(l.to) ? "is-current" : ""}`}>{l.label}</Link>
                            ))}
                            {/* Sprachwahl nur einmal, im Hauptmenü (bleibt auch beim Scrollen sichtbar) */}
                        </nav>
                    </div>
                </div>
            )}

            <div className="rl-nav-row flex items-center justify-between gap-6">
                <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo-link" aria-label="Parto Solution">
                    <img src={logo} alt="Parto Solution" className="rl-nav-logo w-auto" />
                </Link>

                <nav className={`hidden xl:flex items-center ${version === "1" ? "gap-7" : "gap-8"}`} data-testid="nav-desktop-menu" aria-label={lang === "de" ? "Hauptmenü" : "Main menu"}>
                    {links.map((l) => (
                        <div key={l.id} className={`relative ${(version === "3" || version === "4") ? "flex items-center" : ""}`} onMouseEnter={() => setOpenMenu(l.menu || null)} onFocus={(version === "3" || version === "4") ? undefined : () => setOpenMenu(l.menu || null)}>
                            <NavLink
                                to={l.to}
                                data-testid={`nav-link-${l.id}`}
                                aria-haspopup={l.menu && version !== "3" ? "true" : undefined}
                                aria-expanded={l.menu && version !== "3" ? openMenu === l.menu : undefined}
                                aria-current={isGroupActive(l) ? "page" : undefined}
                                className={() =>
                                    `rl-nav-link flex items-center gap-1.5 font-medium text-ink relative py-2 whitespace-nowrap after:absolute after:bottom-0 after:left-0 after:transition-[width] after:duration-300 ${isGroupActive(l) ? "font-bold after:w-full after:h-[2px] after:bg-ember" : "after:bg-current after:w-0 after:h-px hover:after:w-full"}`
                                }
                            >
                                {l.label}
                                {l.menu && version !== "3" && <ChevronDown size={14} className={`transition-transform duration-300 ${openMenu === l.menu ? "rotate-180" : ""}`} />}
                            </NavLink>
                            {/* Version 3: eigener Disclosure-Knopf. V4: ein Pfeil am Namen (wie V1/V2), Mega per Hover. */}
                            {l.menu && version === "3" && (
                                <button
                                    type="button"
                                    ref={(el) => { megaTriggers.current[l.menu] = el; }}
                                    className="nv-v3-toggle"
                                    data-testid={`nav-toggle-${l.id}`}
                                    aria-expanded={openMenu === l.menu}
                                    aria-controls="nv-mega-panel"
                                    aria-label={`${l.label}: ${openMenu === l.menu ? x.ui.collapse : x.ui.expand}`}
                                    onClick={(e) => { if (openMenu === l.menu) setOpenMenu(null); else if (e.detail === 0) openMegaByKey(l.menu); else setOpenMenu(l.menu); }}
                                    onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); openMegaByKey(l.menu); } }}
                                >
                                    <ChevronDown size={14} strokeWidth={1.75} aria-hidden="true" className={`transition-transform duration-300 ${openMenu === l.menu ? "rotate-180" : ""}`} />
                                </button>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="hidden xl:flex items-center gap-2" onMouseEnter={() => setOpenMenu(null)} onFocus={(version === "3" || version === "4") ? () => setOpenMenu(null) : undefined}>
                    {(version === "3" || version === "4") ? (
                        // Version 3: Einstellungen und Sprache als aufklappende Felder, kein Button
                        <>
                            <ViewSettings /><LanguageSettings lang={langUi} onSelect={switchLanguage} />
                            <Link to="/kontakt" data-testid="nav-v3-cta" data-track="self" onClick={v3CtaClick("nav")} className="rl-btn rl-btn-primary rl-nav-cta v3-lead-navcta ml-2 whitespace-nowrap">
                                {v3Cta}
                                <ArrowUpRight size={16} className="arrow" />
                            </Link>
                        </>
                    ) : (
                        <>
                            {controls}
                            <Link to="/contact" data-testid="nav-cta-button" className="rl-btn rl-btn-primary rl-nav-cta ml-2 whitespace-nowrap">
                                {navCta}
                                <ArrowUpRight size={16} className="arrow" />
                            </Link>
                        </>
                    )}
                </div>

                <div className={`flex xl:hidden items-center ${(version === "3" || version === "4") ? "gap-2" : "gap-4"}`}>
                {(version === "3" || version === "4") ? (
                    <div className="flex items-center gap-2">
                        <ViewSettings /><LanguageSettings lang={langUi} onSelect={switchLanguage} />
                        {/* Tablet: Button mit Text. Handy: nur unten im Burger Menü, damit die Leiste bei 360 px nicht abschneidet */}
                        <Link to="/kontakt" data-testid="tablet-nav-v3-cta" data-track="self" onClick={v3CtaClick("nav_tablet")} className="rl-btn rl-btn-primary rl-nav-cta v3-lead-navcta hidden md:inline-flex whitespace-nowrap">
                            {v3Cta}
                            <ArrowUpRight size={16} className="arrow" />
                        </Link>
                    </div>
                ) : (
                <Link to="/contact" data-testid="tablet-nav-cta-button" className="rl-btn rl-btn-primary rl-nav-cta hidden md:inline-flex">
                    {navCta}
                    <ArrowUpRight size={16} className="arrow" />
                </Link>
                )}
                <button
                    data-testid="mobile-menu-toggle"
                    ref={burgerRef}
                    onClick={() => setOpen(!open)}
                    className="xl:hidden text-ink p-2 -mr-2"
                    aria-label={open ? n.close : n.menu}
                    aria-expanded={open}
                    aria-controls="nv-mobile-menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
                </div>
            </div>

            {/* Mega Menü: volle Breite, Einleitung, Spalten mit Kurzbeschreibung, Teaser mit Bild */}
            <AnimatePresence initial={false}>
                {menu && (
                    <motion.div
                        key="mega"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
                        className="hidden xl:block overflow-hidden"
                        data-testid={`nav-menu-${openMenu}`}
                    >
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div key={openMenu} id={(version === "3" || version === "4") ? "nv-mega-panel" : undefined} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2, ease: [0.2, 0.7, 0.2, 1] }} className={`rl-nav-mega nv-mega ${(version === "3" || version === "4") ? "nv-mega-v3" : ""}`}>
                                <div className="nv-mega-intro">
                                    <p className="nv-mega-title">{menu.title}</p>
                                    <p className="nv-mega-lead">{menu.intro}</p>
                                    <Link to={menu.all.to} data-testid={`nav-menu-${openMenu}-all`} className="rl-link mt-6">
                                        {menu.all.label} <ArrowRight size={15} className="arrow" />
                                    </Link>
                                </div>
                                <div className={`nv-mega-cols ${menu.cols.length === 1 ? "is-single" : ""} ${(version === "3" || version === "4") ? `nv-cols-${menu.cols.length}` : ""}`}>
                                    {(() => {
                                        let k = 0;
                                        return menu.cols.map((c) => (
                                            <div key={c.title} className={`nv-col${c.split ? " is-split" : ""}`}>
                                                <p className="nv-col-title">{c.title}</p>
                                                <ul className="nv-list">
                                                    {c.items.map((item) => megaItem(item, `nav-menu-${openMenu}-${k++}`))}
                                                </ul>
                                                {c.sub && (
                                                    <>
                                                        <p className="nv-col-title nv-col-title-sub">{c.sub.title}</p>
                                                        <ul className="nv-list">
                                                            {c.sub.items.map((item) => megaItem(item, `nav-menu-${openMenu}-${k++}`))}
                                                        </ul>
                                                    </>
                                                )}
                                            </div>
                                        ));
                                    })()}
                                </div>
                                {menu.teaser && (
                                    <aside className="nv-teaser" data-testid={`nav-menu-${openMenu}-teaser`}>
                                        {menu.teaser.img && <div className="nv-teaser-media"><img src={menu.teaser.img} alt="" loading="lazy" /></div>}
                                        <div className="nv-teaser-body">
                                            <p className="nv-teaser-title">{menu.teaser.title}</p>
                                            <p className="nv-teaser-text">{menu.teaser.text}</p>
                                            <Link to={menu.teaser.cta.to} data-testid={`nav-menu-${openMenu}-cta`} className="rl-btn rl-btn-primary nv-teaser-cta">
                                                {menu.teaser.cta.label} <ArrowUpRight size={15} className="arrow" />
                                            </Link>
                                        </div>
                                    </aside>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Burger Menü unter 1280 px: Gruppen zum Aufklappen */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="xl:hidden overflow-hidden border-t border-[color:var(--glass-line)]"
                        data-testid="mobile-menu"
                        id="nv-mobile-menu"
                        // Version 3: Burger schließt bei jedem Link Klick, auch wenn das Ziel die aktuelle Seite ist
                        onClickCapture={(version === "3" || version === "4") ? (e) => { if (e.target.closest && e.target.closest("a")) setOpen(false); } : undefined}
                    >
                        <div className="nv-mobile px-6 py-5 flex flex-col max-h-[calc(100dvh-6.5rem)] overflow-y-auto overscroll-contain">
                            {links.map((l) => {
                                const m = l.menu ? menus[l.menu] : null;
                                const expanded = mobileGroup === l.id;
                                let j = 0;
                                return (
                                    <div key={l.id} className="nv-mgroup">
                                        {version === "4" && m ? (
                                        /* Version 4 (30.10.2026, Wunsch Andrej): am Handy führen die Hauptpunkte auf keine Seite, sie klappen nur auf */
                                        <div className="nv-mgroup-head">
                                            <button
                                                type="button"
                                                className={`nv-mgroup-link nv-mgroup-btn ${isGroupActive(l) ? "is-active" : ""}`}
                                                data-testid={`mobile-nav-toggle-${l.id}`}
                                                aria-expanded={expanded}
                                                aria-controls={`nv-mgroup-${l.id}`}
                                                onClick={() => setMobileGroup(expanded ? null : l.id)}
                                            >
                                                <span>{l.label}</span>
                                                <ChevronDown size={18} aria-hidden="true" className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
                                            </button>
                                        </div>
                                        ) : (
                                        <div className="nv-mgroup-head">
                                            <NavLink to={l.to} data-testid={`mobile-nav-link-${l.id}`} aria-current={isGroupActive(l) ? "page" : undefined} className={() => `nv-mgroup-link ${isGroupActive(l) ? "is-active" : ""}`}>
                                                {l.label}
                                            </NavLink>
                                            {m && (
                                                <button
                                                    type="button"
                                                    className="nv-mgroup-toggle"
                                                    data-testid={`mobile-nav-toggle-${l.id}`}
                                                    aria-expanded={expanded}
                                                    aria-controls={`nv-mgroup-${l.id}`}
                                                    aria-label={`${l.label}: ${expanded ? x.ui.collapse : x.ui.expand}`}
                                                    onClick={() => setMobileGroup(expanded ? null : l.id)}
                                                >
                                                    <ChevronDown size={18} className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
                                                </button>
                                            )}
                                        </div>
                                        )}
                                        <AnimatePresence initial={false}>
                                            {m && expanded && (
                                                <motion.div
                                                    id={`nv-mgroup-${l.id}`}
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="nv-mgroup-body">
                                                        {m.cols.flatMap((c) => [{ title: c.title, items: c.items }, ...(c.sub ? [c.sub] : [])]).map((sec) => (
                                                            <div key={sec.title} className="nv-msec">
                                                                <p className="nv-col-title">{sec.title}</p>
                                                                {sec.items.map((child) => {
                                                                    const idx = j++;
                                                                    return (
                                                                        <Link key={child.to} to={child.to} data-testid={`mobile-nav-${l.id}-sub-${idx}`} aria-current={isCurrent(child.to) ? "page" : undefined} className={`nv-mlink ${isCurrent(child.to) ? "is-current" : ""}${child.kind ? ` is-${child.kind}` : ""}`}>
                                                                            {child.label}
                                                                        </Link>
                                                                    );
                                                                })}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                );
                            })}

                            {SHOW_UTIL_BAR && util && (
                                <div className="nv-mutil" data-testid="mobile-nav-utility">
                                    {util.links.map((l) => (
                                        <Link key={l.to} to={l.to} className="nv-mutil-link">{l.label}</Link>
                                    ))}
                                    <Link to="/brands" className="nv-mutil-link">{util.brandsHint}</Link>
                                </div>
                            )}

                            {(version === "3" || version === "4") && (
                                <Link to="/kontakt" data-testid="mobile-nav-v3-cta" data-track="self" onClick={v3CtaClick("nav_burger")} className="v3-lead-mcta">
                                    {v3Cta} <ArrowUpRight size={16} aria-hidden="true" />
                                </Link>
                            )}

                            {version !== "3" && (
                            <div className="flex items-center gap-3 pt-5 mt-4 border-t border-petrol-700/70">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={theme === "dark"}
                                    aria-label={themeLabel}
                                    data-testid="mobile-theme-switch"
                                    onClick={toggleTheme}
                                    className="nv-switch relative h-10 w-[68px] shrink-0 border border-petrol-700 bg-petrol-800"
                                >
                                    <span className={`nv-switch-knob absolute top-[3px] bottom-[3px] w-[30px] bg-brand transition-[left] duration-300 ${theme === "dark" ? "left-[35px]" : "left-[3px]"}`} aria-hidden="true" />
                                    <Sun size={14} className={`absolute left-[11px] top-1/2 -translate-y-1/2 z-10 ${theme === "light" ? "text-white" : "text-petrol-400"}`} />
                                    <Moon size={13} className={`absolute right-[11px] top-1/2 -translate-y-1/2 z-10 ${theme === "dark" ? "text-white" : "text-petrol-400"}`} />
                                </button>
                                <div className="nv-mlang flex items-center border border-petrol-700 text-xs font-semibold h-10" data-testid="mobile-language-toggle">
                                    <button data-testid="mobile-lang-de-button" aria-pressed={langUi === "de"} onClick={() => switchLanguage("de")} className={`px-3 h-full ${langUi === "de" ? "bg-brand text-white" : "text-petrol-300"}`}>DE</button>
                                    <button data-testid="mobile-lang-en-button" aria-pressed={langUi === "en"} onClick={() => switchLanguage("en")} className={`px-3 h-full ${langUi === "en" ? "bg-brand text-white" : "text-petrol-300"}`}>EN</button>
                                </div>
                                <Link to="/contact" data-testid="mobile-nav-cta-button" className="nv-mcta flex-1 inline-flex items-center justify-center whitespace-nowrap bg-brand text-white text-[13px] font-semibold h-10 px-3">
                                    {navCta}
                                </Link>
                            </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
        </div>
        </>
    );
};

export default NavbarLegacy;
